import { useState, type FormEvent } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { latestUpdates, categories } from "@/mocks/events";
import { siteInfo } from "@/mocks/site";

const navItems = [
  { label: "首页", to: "/" },
  { label: "热点总览", to: "/events" },
  { label: "关于我们", to: "/about" },
];

export default function SiteHeader() {
  const navigate = useNavigate();
  const [catOpen, setCatOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [keyword, setKeyword] = useState("");

  const submitSearch = (event: FormEvent) => {
    event.preventDefault();
    const value = keyword.trim();
    if (!value) return;
    setSearchOpen(false);
    setMobileOpen(false);
    navigate(`/events?q=${encodeURIComponent(value)}`);
  };

  const tickerLoop = [...latestUpdates, ...latestUpdates];

  return (
    <>
      <div className="bg-foreground-950 text-background-100">
        <div className="max-w-shell mx-auto flex items-center gap-3 px-4 md:px-6 h-9">
          <span className="inline-flex items-center gap-1.5 shrink-0 text-[11px] font-medium tracking-widest text-accent-400">
            <span className="w-1.5 h-1.5 rounded-full bg-primary-400 animate-pulseDot" />
            实时更新
          </span>
          <div className="relative flex-1 overflow-hidden">
            <div className="ticker-track flex w-max whitespace-nowrap">
              {tickerLoop.map((item, index) => (
                <span key={`${item.time}-${index}`} className="mx-5 text-xs text-background-200/90">
                  <span className="text-accent-400 mr-1.5">{item.time}</span>
                  {item.text}
                </span>
              ))}
            </div>
          </div>
          <Link
            to="/events"
            className="hidden md:inline-flex items-center gap-1 shrink-0 text-[11px] text-background-200 hover:text-accent-400 transition-colors cursor-pointer whitespace-nowrap"
          >
            全部时间线
            <i className="ri-arrow-right-s-line" />
          </Link>
        </div>
      </div>

      <header className="sticky top-0 z-50 bg-background-50/92 backdrop-blur border-b border-background-200">
        <div className="max-w-shell mx-auto flex items-center justify-between gap-4 px-4 md:px-6 h-16">
          <Link to="/" className="flex items-center gap-2.5 shrink-0 cursor-pointer">
            <span className="w-9 h-9 rounded-md bg-primary-500 text-background-50 flex items-center justify-center font-heading font-black text-lg">
              爆
            </span>
            <span className="flex flex-col leading-none">
              <span className="font-heading text-lg font-bold tracking-wide text-foreground-950">
                {siteInfo.name}
              </span>
              <span className="hidden sm:block text-[11px] text-foreground-500 mt-1">
                {siteInfo.tagline}
              </span>
            </span>
          </Link>

          <nav className="hidden md:flex items-center gap-1">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === "/"}
                className={({ isActive }) =>
                  `px-3.5 py-2 rounded-md text-sm transition-colors cursor-pointer whitespace-nowrap ${
                    isActive
                      ? "text-primary-700 bg-primary-50 font-medium"
                      : "text-foreground-700 hover:text-foreground-950 hover:bg-background-100"
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
            <div className="relative">
              <button
                type="button"
                onClick={() => setCatOpen((prev) => !prev)}
                className="px-3.5 py-2 rounded-md text-sm text-foreground-700 hover:text-foreground-950 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap inline-flex items-center gap-1"
              >
                分类专题
                <i className={`ri-arrow-down-s-line transition-transform ${catOpen ? "rotate-180" : ""}`} />
              </button>
              {catOpen ? (
                <>
                  <div className="fixed inset-0 z-10" onClick={() => setCatOpen(false)} />
                  <div className="absolute right-0 top-full mt-2 z-20 w-[420px] p-3 rounded-lg bg-background-50 border border-background-200 shadow-sm">
                    <div className="grid grid-cols-2 gap-1.5">
                      {categories.map((cat) => (
                        <Link
                          key={cat.slug}
                          to={`/events?cat=${cat.slug}`}
                          onClick={() => setCatOpen(false)}
                          className="flex items-start gap-2.5 p-2.5 rounded-md hover:bg-background-100 transition-colors cursor-pointer"
                        >
                          <span className="w-8 h-8 rounded-md bg-secondary-100 text-secondary-700 flex items-center justify-center shrink-0">
                            <i className={cat.icon} />
                          </span>
                          <span className="flex flex-col">
                            <span className="text-sm font-medium text-foreground-900">{cat.name}</span>
                            <span className="text-[11px] text-foreground-500 mt-0.5 leading-snug">
                              {cat.desc}
                            </span>
                          </span>
                        </Link>
                      ))}
                    </div>
                  </div>
                </>
              ) : null}
            </div>
          </nav>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setSearchOpen((prev) => !prev)}
              aria-label="搜索爆料"
              className="w-9 h-9 rounded-md border border-background-300 text-foreground-700 flex items-center justify-center hover:bg-background-100 hover:text-foreground-950 transition-colors cursor-pointer"
            >
              <i className={searchOpen ? "ri-close-line" : "ri-search-line"} />
            </button>
            <Link
              to="/about"
              className="hidden sm:inline-flex items-center gap-1.5 px-4 py-2 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-send-plane-line" />
              投递线索
            </Link>
            <button
              type="button"
              onClick={() => setMobileOpen((prev) => !prev)}
              aria-label="打开菜单"
              className="md:hidden w-9 h-9 rounded-md border border-background-300 text-foreground-700 flex items-center justify-center cursor-pointer"
            >
              <i className={mobileOpen ? "ri-close-line" : "ri-menu-line"} />
            </button>
          </div>
        </div>

        {searchOpen ? (
          <div className="border-t border-background-200 bg-background-100">
            <form
              onSubmit={submitSearch}
              className="max-w-shell mx-auto flex items-center gap-2 px-4 md:px-6 py-3"
            >
              <span className="w-9 h-9 flex items-center justify-center text-foreground-500 shrink-0">
                <i className="ri-search-line" />
              </span>
              <input
                value={keyword}
                onChange={(event) => setKeyword(event.target.value)}
                placeholder="搜索事件关键词，如「退费」「续航」「换角」"
                className="flex-1 h-10 px-3 rounded-md bg-background-50 border border-background-300 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400"
              />
              <button
                type="submit"
                className="h-10 px-4 rounded-md bg-foreground-950 text-background-50 text-sm hover:bg-foreground-800 transition-colors cursor-pointer whitespace-nowrap"
              >
                搜索
              </button>
            </form>
          </div>
        ) : null}

        {mobileOpen ? (
          <div className="md:hidden border-t border-background-200 bg-background-50">
            <div className="px-4 py-4 flex flex-col gap-4">
              <nav className="flex flex-col gap-1">
                {navItems.map((item) => (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    end={item.to === "/"}
                    onClick={() => setMobileOpen(false)}
                    className={({ isActive }) =>
                      `px-3 py-2.5 rounded-md text-sm cursor-pointer ${
                        isActive
                          ? "bg-primary-50 text-primary-700 font-medium"
                          : "text-foreground-700 hover:bg-background-100"
                      }`
                    }
                  >
                    {item.label}
                  </NavLink>
                ))}
              </nav>
              <div>
                <p className="text-xs tracking-widest text-foreground-500 mb-2">分类专题</p>
                <div className="flex flex-wrap gap-2">
                  {categories.map((cat) => (
                    <Link
                      key={cat.slug}
                      to={`/events?cat=${cat.slug}`}
                      onClick={() => setMobileOpen(false)}
                      className="px-3 py-1.5 rounded-full bg-background-100 border border-background-200 text-xs text-foreground-700 cursor-pointer"
                    >
                      {cat.name}
                    </Link>
                  ))}
                </div>
              </div>
              <Link
                to="/about"
                onClick={() => setMobileOpen(false)}
                className="inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-medium cursor-pointer"
              >
                <i className="ri-send-plane-line" />
                投递线索
              </Link>
            </div>
          </div>
        ) : null}
      </header>
    </>
  );
}